import { Component } from './component.js';
import { ThemeEvents } from './events.js';

/**
 * Sticky Add to Cart — mobile floating button + desktop floating card.
 * Both surfaces share one IntersectionObserver, one variant-update path,
 * and one engraving-aware price calculator.
 */
class StickyAddToCartBar extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.enabled = this.dataset.enabled === 'true';
    if (!this.enabled) return;

    requestAnimationFrame(() => {
      // Mobile elements
      this.button = this.querySelector('.sticky-add-to-cart-button');
      this.priceEl = this.button?.querySelector('.sticky-button-price');
      this.buttonText = this.button?.querySelector('.sticky-button-text');
      this.form = this.querySelector('.sticky-add-to-cart-form');
      this.hiddenInput = this.form?.querySelector('input[name="id"]');

      // Desktop card elements
      this.card = this.querySelector('.sticky-add-to-cart-card');
      this.cardCta = this.card?.querySelector('.sticky-card-cta');
      this.cardCtaText = this.card?.querySelector('.sticky-card-cta-text');
      this.cardPriceEl = this.card?.querySelector('.sticky-card-price');
      this.cardCtaPriceEl = this.card?.querySelector('.sticky-card-cta-price');

      // Pricing state (cents for base, dollars for fee — matches metafield format)
      this.basePrice = parseInt(this.dataset.basePrice || '0', 10);
      this.engraveFeeOne = parseInt(this.dataset.engraveFeeOne || '0', 10);
      this.engraveFeeTwo = parseInt(this.dataset.engraveFeeTwo || '0', 10);
      this.engraveFee = 0;

      if (!this.button && !this.card) return;

      this.setupIntersectionObserver();
      this.bindEvents();
      this.bindEngravingListeners();
      this.refreshPrice();
    });
  }

  setupIntersectionObserver() {
    const mainButton = document.querySelector('.buy-buttons-block button[type="submit"]');
    if (!mainButton) {
      console.log('Sticky bar: main add-to-cart button not found');
      return;
    }

    // Trigger off the *whole* Product information section, not the ATC button alone.
    // The section is tall (gallery + buy-box + accordions + Complete Your Setup),
    // so observing the button alone would show the sticky while the user is
    // still reading inside the section. Walking up from the main ATC finds
    // the right section regardless of theme template.
    const triggerEl =
      mainButton.closest('.shopify-section') ||
      mainButton.closest('shopify-section') ||
      mainButton;

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Section has scrolled fully above viewport: not intersecting AND its
          // bounding box top is negative. (If top > 0, we haven't reached it yet.)
          const rect = entry.boundingClientRect;
          const scrolledPast = !entry.isIntersecting && rect.top < 0;
          this.setVisible(scrolledPast);
        });
      },
      {
        threshold: 0,
      }
    );

    this.observer.observe(triggerEl);
  }

  setVisible(shouldShow) {
    const val = shouldShow ? 'true' : 'false';
    if (this.button && this.button.getAttribute('data-visible') !== val) {
      this.button.setAttribute('data-visible', val);
    }
    if (this.card && this.card.getAttribute('data-visible') !== val) {
      this.card.setAttribute('data-visible', val);
      this.card.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }
  }

  bindEvents() {
    // Mobile form submit
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        e.preventDefault();
        this.handleAddToCart();
      });
    }

    // Desktop card button
    if (this.cardCta) {
      this.cardCta.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleAddToCart();
      });
    }

    // Variant updates from the main PDP form
    const closestSection = this.closest('.shopify-section, [data-section-id]');
    if (closestSection) {
      closestSection.addEventListener(ThemeEvents.variantUpdate, (event) => {
        this.updateVariant(event);
      });
    }
  }

  /**
   * Listen for engraving toggle clicks on the PDP and recompute price.
   * Uses event delegation so we don't depend on toggle DOM existing at boot.
   */
  bindEngravingListeners() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest(
        '#btn-no-engraving, #btn-add-engraving, #btn-one-line, #btn-two-lines'
      );
      if (!btn) return;
      // Defer one tick so the engraving block can update its .selected class first
      setTimeout(() => this.recomputeEngravingFee(), 0);
    });
  }

  recomputeEngravingFee() {
    const isEngraveOn = document
      .getElementById('btn-add-engraving')
      ?.classList.contains('selected');
    const isTwoLines = document
      .getElementById('btn-two-lines')
      ?.classList.contains('selected');

    let fee = 0;
    if (isEngraveOn) {
      fee = isTwoLines ? this.engraveFeeTwo : this.engraveFeeOne;
    }
    if (fee !== this.engraveFee) {
      this.engraveFee = fee;
      this.refreshPrice();
    }
  }

  /**
   * Render the engraving-aware total price across mobile + desktop surfaces.
   * basePrice is in cents; engraveFee is in dollars (matches knife_num × 19/29).
   */
  refreshPrice() {
    const total = this.basePrice + this.engraveFee * 100;
    const formatted = this.formatMoney(total);

    if (this.priceEl) this.priceEl.textContent = formatted;
    if (this.cardPriceEl) this.cardPriceEl.textContent = formatted;
    if (this.cardCtaPriceEl) this.cardCtaPriceEl.textContent = formatted;
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(
        cents,
        (typeof theme !== 'undefined' && theme.moneyFormat) || '${{amount}}'
      );
    }
    return `$${(cents / 100).toFixed(2)}`;
  }

  /**
   * Forward the cart-add to the main product form so cart drawer / animations
   * / property line items (engraving text, etc.) all behave the same.
   */
  async handleAddToCart() {
    const mainForm = document.querySelector('form[data-type="add-to-cart-form"]');
    if (!mainForm) return;

    const mainVariantInput = mainForm.querySelector('input[name="id"]');
    if (mainVariantInput && this.hiddenInput) {
      mainVariantInput.value = this.hiddenInput.value;
    }

    const submitButton = mainForm.querySelector('button[type="submit"]');
    if (submitButton) submitButton.click();
  }

  updateVariant(event) {
    const variant = event.detail.resource;
    if (!variant) return;

    this.basePrice = variant.price;
    this.refreshPrice();

    if (this.hiddenInput) {
      this.hiddenInput.value = variant.id;
    }

    // Purchase state comes from the freshly rendered Liquid (data-purchase-state
    // on the new sticky bar, only present when not plain buyable):
    // variant.available alone can't distinguish pre-order from fail-closed
    // continue-policy variants (both report true).
    const freshBar = event.detail.data?.html?.querySelector('sticky-add-to-cart-bar');
    const state = freshBar?.dataset.purchaseState || (variant.available ? 'buyable' : 'soldout');
    this.setAvailability(state);
  }

  setAvailability(state) {
    const available = state === 'buyable' || state === 'preorder';
    if (this.button) this.button.disabled = !available;
    if (this.cardCta) this.cardCta.disabled = !available;
    const label = state === 'preorder' ? 'Pre-order' : available ? 'Add to Cart' : 'Sold Out';
    if (this.buttonText) this.buttonText.textContent = label;
    if (this.cardCtaText) this.cardCtaText.textContent = label;
  }

  disconnectedCallback() {
    if (this.observer) this.observer.disconnect();
  }
}

customElements.define('sticky-add-to-cart-bar', StickyAddToCartBar);
